/**
 * Detect a streaming platform from any URL and return an embeddable iframe src.
 * Returns { platform, embed_url, watch_url, label } or null if it can't be embedded.
 *
 * Supports YouTube (live + standard), Twitch, Facebook (live + watch), Instagram (live link),
 * Vimeo, and a generic fallback for any HTTPS URL containing "live".
 */
export function detectStreamUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();
  if (!url) return null;
  let parsed;
  try {
    parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
  const path = parsed.pathname;
  const params = parsed.searchParams;

  // ---- YouTube ----
  // youtu.be/<id>
  // youtube.com/watch?v=<id>
  // youtube.com/live/<id>
  // youtube.com/@channel/live  → unembeddable directly, fall back to channel embed
  // youtube.com/embed/<id>
  if (host === 'youtu.be') {
    const id = path.replace(/^\//, '').split('/')[0];
    if (id) return { platform: 'youtube', label: 'YouTube', watch_url: url,
      embed_url: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
  }
  if (host.endsWith('youtube.com') || host === 'm.youtube.com') {
    if (path === '/watch' && params.get('v')) {
      return { platform: 'youtube', label: 'YouTube', watch_url: url,
        embed_url: `https://www.youtube.com/embed/${params.get('v')}?autoplay=1&rel=0` };
    }
    if (path.startsWith('/live/')) {
      const id = path.split('/')[2];
      if (id) return { platform: 'youtube', label: 'YouTube Live', watch_url: url,
        embed_url: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
    }
    if (path.startsWith('/embed/')) {
      const id = path.split('/')[2];
      if (id) return { platform: 'youtube', label: 'YouTube', watch_url: url,
        embed_url: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
    }
    // Channel live page (e.g. /@somecoach/live) — fall through, can't embed channel pages safely
  }

  // ---- Twitch ----
  // twitch.tv/<channel>
  if (host.endsWith('twitch.tv')) {
    const channel = path.replace(/^\//, '').split('/')[0];
    if (channel) {
      const parent = (typeof window !== 'undefined' && window.location.hostname) || 'localhost';
      return { platform: 'twitch', label: 'Twitch', watch_url: url,
        embed_url: `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${encodeURIComponent(parent)}&muted=false` };
    }
  }

  // ---- Facebook Live / Watch ----
  // fb.watch/<id> · facebook.com/<page>/videos/<id> · facebook.com/watch?v=<id>
  if (host === 'fb.watch' || host.endsWith('facebook.com')) {
    return { platform: 'facebook', label: 'Facebook', watch_url: url,
      embed_url: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=1` };
  }

  // ---- Instagram ----
  // instagram.com/p/<id>/  ·  /reel/<id>/  ·  /tv/<id>/
  if (host.endsWith('instagram.com')) {
    return { platform: 'instagram', label: 'Instagram', watch_url: url,
      // Instagram embed = /embed/ on the same path
      embed_url: `https://www.instagram.com${path}embed` };
  }

  // ---- Vimeo ----
  // vimeo.com/<id>  · vimeo.com/event/<id>
  if (host.endsWith('vimeo.com')) {
    if (path.startsWith('/event/')) {
      const id = path.split('/')[2];
      if (id) return { platform: 'vimeo', label: 'Vimeo', watch_url: url,
        embed_url: `https://vimeo.com/event/${id}/embed` };
    }
    const id = path.replace(/^\//, '').split('/')[0];
    if (/^\d+$/.test(id)) {
      return { platform: 'vimeo', label: 'Vimeo', watch_url: url,
        embed_url: `https://player.vimeo.com/video/${id}?autoplay=1` };
    }
  }

  // ---- Fallback: any direct .m3u8 / .mp4 ----
  if (/\.(m3u8|mp4)(\?|#|$)/i.test(url)) {
    return { platform: 'direct', label: 'Live Stream', watch_url: url,
      embed_url: url };
  }

  // ---- Unknown — return watch_url only so we can offer a "Watch on …" link ----
  return { platform: 'unknown', label: 'External link', watch_url: url, embed_url: null };
}
